import getFromCdn from '@anm/helpers/getFromCdn';
import { media, truncate, truncateMultiline } from '@anm/styles/helpers';
import styled, { css } from 'styled-components';

import { EditableFieldHoverVariants, EditableFieldSizes, EditableFieldVariants } from '.';

type FieldInputProps = {
  fieldSize: EditableFieldSizes;
  isActive?: boolean;
  hoverVariant?: EditableFieldHoverVariants;
};

type FieldProps = FieldInputProps & {
  canShow: boolean;
  variant: EditableFieldVariants;
  isFocusedOnClick: boolean;
  ownHeight?: number;
};

const cropStyle = css`
  display: flex;
  align-items: center;

  & > * {
    display: block;
    ${truncate('unset')}
  }
`;

const cropMultilineStyle = css`
  & > * {
    ${truncateMultiline('2')}
  }
`;

const fieldVariants: {
  [key in EditableFieldVariants]: ReturnType<typeof css>;
} = {
  crop: cropStyle,
  multirows: css``,
  cropMultiline: cropMultilineStyle
};

const borderHoverStyles = css`
  left: 0;
  content: '';
  height: 1px;
  position: absolute;
  width: calc(100% - 27px);
  border-bottom: 1px dashed #919eaa;
`;

const fieldPencilHover = css<FieldProps>`
  span {
    word-break: break-word;
    padding-right: 27px;
  }
  &:hover span {
    overflow: hidden;
    position: relative;
    max-width: fit-content;
    background: url(${getFromCdn('images/dashboard/edit_v2.svg')}) no-repeat;
    background-position: calc(100% - 1px) calc(100% - 2px);
    &:before {
      top: ${({ ownHeight }) => (ownHeight && ownHeight > 30 ? ownHeight / 2 - 1 : 21)}px;
      ${borderHoverStyles};
    }
    &:after {
      bottom: 0;
      ${borderHoverStyles};
    }
  }
`;

const fieldHoverVariants: {
  [key in EditableFieldHoverVariants]: ReturnType<typeof css>;
} = {
  pencil: fieldPencilHover
};

const smallFieldSizeAdaptiveStyles = media.tablet`
  font-size: 14px;
  padding-left: 4px;
`;

const fieldSizes: { [key in EditableFieldSizes]: ReturnType<typeof css> } = {
  small: css`
    font-size: 16px;
    line-height: 22px;
    padding: 0 0 1px 5px;
    background-clip: padding-box;
    letter-spacing: -0.1px;
    font-weight: 600;
    ${smallFieldSizeAdaptiveStyles};
  `,
  medium: css`
    font-size: 20px;
    line-height: 24px;
    padding: 1px 5px;
    background-clip: padding-box;
    font-weight: bold;
    letter-spacing: -0.1px;
    &:after {
      width: 18px;
      height: 18px;
      transform: translateX(10px);
    }
  `
};

export const FieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const Field = styled.div<FieldProps>`
  cursor: pointer;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid transparent;
  & > * {
    margin: 0;
    font-size: inherit;
    opacity: ${({ canShow }) => (canShow ? 0 : 1)};
  }

  &:hover {
    outline: none;
    cursor: ${({ isFocusedOnClick }) => (isFocusedOnClick ? 'pointer' : 'auto')};

    &:after {
      opacity: 1;
    }
  }

  ${({ fieldSize }) => fieldSizes[fieldSize]}
  ${({ variant }) => fieldVariants[variant]}
  ${({ hoverVariant, isFocusedOnClick }) => isFocusedOnClick && hoverVariant && fieldHoverVariants[hoverVariant]}
`;

const inputHoverStyles = css`
  padding-right: 30px;
`;

export const FieldInput = styled.div<FieldInputProps>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  resize: none;
  color: #292a2b;
  font-weight: 600;
  background: #fff;
  letter-spacing: -0.1px;
  border: 1px solid transparent;
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  pointer-events: ${({ isActive }) => (isActive ? 'all' : 'none')};
  &::selection {
    background: rgba(32, 171, 210, 0.2);
  }
  &:focus {
    outline: none;
    border-radius: 3px;
    border: 1px solid var(--blue_100);
  }
  ${({ fieldSize }) => fieldSizes[fieldSize]};
  ${({ hoverVariant }) => hoverVariant && inputHoverStyles};
`;

export const ValueHolder = styled.span`
  color: ${({ theme }) => theme.textColor};
`;
